import React from 'react';
import {
  Dialog,
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import history from '../../utilities/history';
import SendCreditAppDialog from '../../components/SendCreditAppDialog';
import CreditAppCarPickerDialog from '../../components/CreditAppCarPickerDialog';
import CreditAppSelectorDialog from '../../components/CreditAppSelectorDialog';
import store from '../../utilities/store';
import { useAutoZ, ModalStackProvider, useSelectMenuProps } from '../../utilities/ModalStackContext';
import { useTallDialogSizing } from '../../utilities/dialogSizing';
import moment from 'moment';

const STATUSES = ['pending', 'reviewed', 'sent', 'approved', 'declined'];

const STATUS_COLORS = {
  pending:  '#ff9800',
  reviewed: '#2196f3',
  sent:     '#9c27b0',
  approved: '#4caf50',
  declined: '#f44336',
};

const SECTIONS = [
  {
    title: 'Personal Information',
    fields: [
      { id: 'first_name',   label: 'First Name' },
      { id: 'last_name',    label: 'Last Name' },
      { id: 'email',        label: 'Email' },
      { id: 'phone_number', label: 'Phone Number' },
      { id: 'ssn',          label: 'SSN' },
      { id: 'birthday',     label: 'Date of Birth' },
    ],
  },
  {
    title: 'Current Address',
    fields: [
      { id: 'buyer_address', label: 'Address' },
      { id: 'buyer_city',    label: 'City' },
      { id: 'buyer_state',   label: 'State' },
      { id: 'buyer_zip',     label: 'ZIP' },
      { id: 'address_years', label: 'Years at Address', type: 'number' },
      { id: 'own_or_rent',   label: 'Own or Rent' },
    ],
  },
  {
    title: 'Previous Address',
    fields: [
      { id: 'prev_address',      label: 'Previous Address' },
      { id: 'prev_city',         label: 'City' },
      { id: 'prev_state',        label: 'State' },
      { id: 'prev_zip',          label: 'ZIP' },
      { id: 'address_years_prev', label: 'Years at Address', type: 'number' },
    ],
  },
  {
    title: 'Employment',
    fields: [
      { id: 'employer',       label: 'Employer' },
      { id: 'position',       label: 'Position' },
      { id: 'job_years',      label: 'Years at Job', type: 'number' },
      { id: 'employer_prev',  label: 'Previous Employer' },
      { id: 'position_prev',  label: 'Previous Position' },
      { id: 'job_years_prev', label: 'Years at Previous Job', type: 'number' },
    ],
  },
  {
    title: 'Financial Information',
    fields: [
      { id: 'income',              label: 'Monthly Income', type: 'number' },
      { id: 'income_other',        label: 'Other Income', type: 'number' },
      { id: 'income_other_source', label: 'Other Income Source' },
      { id: 'housing',             label: 'Monthly Housing Cost', type: 'number' },
      { id: 'downpayment',         label: 'Down Payment', type: 'number' },
    ],
  },
  {
    title: 'Credit History',
    fields: [
      { id: 'bankruptcy',   label: 'Bankruptcy' },
      { id: 'repossession', label: 'Repossession' },
      { id: 'foreclosure',  label: 'Foreclosure' },
    ],
  },
];

export default function CreditAppModal({ open, creditAppId, onClose }) {
  const id = creditAppId;
  const myZ = useAutoZ(open, creditAppId);
  const { fullScreen, paperSx } = useTallDialogSizing({ height: '95vh', margin: 1.5, radius: 2 });
  const theme = useTheme();
  const selectMenuProps = useSelectMenuProps(myZ);

  const [app, setApp] = React.useState(null);
  const [edits, setEdits] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [licenseUrls, setLicenseUrls] = React.useState({ front: null, back: null });
  const [vehicleLinking, setVehicleLinking] = React.useState(false);
  const [carPickerOpen, setCarPickerOpen] = React.useState(false);
  const [sendOpen, setSendOpen] = React.useState(false);
  const [coApplicant, setCoApplicant] = React.useState(null);
  const [coApplicantPickerOpen, setCoApplicantPickerOpen] = React.useState(false);
  // A deal (car) whose buyer or cobuyer is this app's customer. When set, the
  // car/co-buyer are supplied automatically at send time (SendToLendersDialog),
  // so the manual link controls below are redundant and hidden in favor of this.
  const [dealMatch, setDealMatch] = React.useState(null);

  React.useEffect(() => {
    // Clear state whenever the target app changes (including when it becomes null on close)
    setApp(null);
    setEdits({});
    setLicenseUrls({ front: null, back: null });
    setCoApplicant(null);
    setDealMatch(null);

    if (!id) return;

    let cancelled = false;

    async function fetchData() {
      const db = firebase.firestore();
      const doc = await db.doc(`credit-apps/${id}`).get();
      if (cancelled || !doc.exists) return;

      const data = { id: doc.id, ...doc.data() };
      setApp(data);

      // Load the linked co-applicant (co-buyer) app, if any
      if (data.co_applicant) {
        const coDoc = await db.doc(`credit-apps/${data.co_applicant}`).get();
        if (!cancelled) setCoApplicant(coDoc.exists ? { id: coDoc.id, ...coDoc.data() } : null);
      }

      // Is this app's customer already the buyer or cobuyer on a deal? If so,
      // that deal supplies the car/co-buyer automatically — no manual link needed.
      if (data.customer) {
        const [asBuyer, asCobuyer] = await Promise.all([
          db.collection('deals').where('buyer', '==', data.customer).limit(1).get(),
          db.collection('deals').where('cobuyer', '==', data.customer).limit(1).get(),
        ]);
        const dealDoc = !asBuyer.empty ? asBuyer.docs[0] : (!asCobuyer.empty ? asCobuyer.docs[0] : null);
        if (!cancelled && dealDoc) {
          const dealData = dealDoc.data() || {};
          setDealMatch({ stock: dealDoc.id, buyer: dealData.buyer, cobuyer: dealData.cobuyer });
        }
      }

      // Load license image URLs from the linked customer document
      if (data.customer) {
        const customerDoc = await db.doc(`customers/${data.customer}`).get();
        if (cancelled || !customerDoc.exists) return;
        const customer = customerDoc.data();
        const storageRef = firebase.storage();
        const urls = { front: null, back: null };

        if (customer.license) {
          try { urls.front = await storageRef.ref(customer.license).getDownloadURL(); } catch (e) { /* no front license */ }
        }
        if (customer.license_back) {
          try { urls.back = await storageRef.ref(customer.license_back).getDownloadURL(); } catch (e) { /* no back license */ }
        }
        if (!cancelled) setLicenseUrls(urls);
      }
    }
    fetchData();

    return () => { cancelled = true; };
  }, [id]);

  const getValue = (fieldId) => {
    if (fieldId in edits) return edits[fieldId];
    return app?.[fieldId] ?? '';
  };

  const handleChange = (fieldId, value) => {
    setEdits(prev => ({ ...prev, [fieldId]: value }));
  };

  const hasEdits = Object.keys(edits).length > 0;

  const handleSelectCar = async (car) => {
    const stock = (car.stock || car.objectID || '').toUpperCase();
    if (!stock) return;
    setCarPickerOpen(false);
    setVehicleLinking(true);
    try {
      const carTitle = `${car.year || ''} ${car.make || ''} ${car.model || ''}`.trim();
      const update = {
        stock,
        car_title: carTitle,
        car_thumbnail: car.thumbnail || '',
      };
      await firebase.firestore().doc(`credit-apps/${id}`).set(update, { merge: true });
      setApp(prev => ({ ...prev, ...update }));
      StateManager.setAlertAndOpen(`Linked to ${carTitle || stock}`, 'success');
    } catch (e) {
      console.error(e);
      StateManager.setAlertAndOpen('Error linking vehicle', 'error');
    } finally {
      setVehicleLinking(false);
    }
  };

  const handleLinkCoApplicant = async (otherApp) => {
    setCoApplicantPickerOpen(false);
    if (!otherApp || otherApp.id === id) {
      StateManager.setAlertAndOpen('Pick a different credit app as the co-applicant', 'warning');
      return;
    }
    try {
      await store.linkCoApplicant(id, otherApp.id);
      setApp(prev => ({ ...prev, co_applicant: otherApp.id }));
      setCoApplicant(otherApp);
      StateManager.setAlertAndOpen('Co-applicant linked', 'success');
    } catch (e) {
      StateManager.setAlertAndOpen('Error linking co-applicant', 'error');
    }
  };

  const handleUnlinkCoApplicant = async () => {
    try {
      await store.unlinkCoApplicant(id);
      setApp(prev => { const next = { ...prev }; delete next.co_applicant; return next; });
      setCoApplicant(null);
      StateManager.setAlertAndOpen('Co-applicant unlinked', 'success');
    } catch (e) {
      StateManager.setAlertAndOpen('Error unlinking co-applicant', 'error');
    }
  };

  const handleUnlinkVehicle = async () => {
    try {
      await firebase.firestore().doc(`credit-apps/${id}`).update({
        stock: firebase.firestore.FieldValue.delete(),
        car_title: firebase.firestore.FieldValue.delete(),
        car_thumbnail: firebase.firestore.FieldValue.delete(),
      });
      setApp(prev => { const next = { ...prev }; delete next.stock; delete next.car_title; delete next.car_thumbnail; return next; });
      StateManager.setAlertAndOpen('Vehicle unlinked', 'success');
    } catch (e) {
      StateManager.setAlertAndOpen('Error unlinking vehicle', 'error');
    }
  };

  const handleSave = async () => {
    if (!app) return;
    setSaving(true);
    try {
      await firebase.firestore().doc(`credit-apps/${id}`).set(edits, { merge: true });

      // Sync contact and address fields to the linked customer document
      if (app.customer) {
        const CUSTOMER_FIELD_MAP = {
          first_name:    'first_name',
          last_name:     'last_name',
          email:         'email',
          phone_number:  'phone_number',
          buyer_address: 'address1',
          buyer_city:    'city',
          buyer_state:   'state',
          buyer_zip:     'zip',
          birthday:      'birthday',
        };
        const customerUpdates = {};
        for (const [appField, customerField] of Object.entries(CUSTOMER_FIELD_MAP)) {
          if (appField in edits) customerUpdates[customerField] = edits[appField];
        }
        if (Object.keys(customerUpdates).length > 0) {
          await firebase.firestore().doc(`customers/${app.customer}`).set(customerUpdates, { merge: true });
        }
      }

      setApp({ ...app, ...edits });
      setEdits({});
      StateManager.setAlertAndOpen('Saved', 'success');
    } catch (e) {
      console.error(e);
      StateManager.setAlertAndOpen('Error saving', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => { if (onClose) onClose(); };

  const currentStatus = getValue('status') || 'pending';
  const statusColor = STATUS_COLORS[currentStatus] || '#9e9e9e';
  const headerName = app ? `${app.first_name || ''} ${app.last_name || ''}`.trim() : '';

  return (
    <ModalStackProvider value={myZ}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        sx={{ zIndex: myZ }}
        PaperProps={{
          sx: {
            ...paperSx,
            overflow: 'hidden',
          },
        }}
      >
        {/* ── Top bar ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {app ? `Credit App${headerName ? '  ·  ' + headerName : ''}` : 'Loading…'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={handleClose} size="small" sx={{ color: theme.palette.text.secondary }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── Scrollable content ── */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {!app ? (
            <Grid container justifyContent="center" style={{ padding: 60 }}>
              <CircularProgress />
            </Grid>
          ) : (
            <Grid container spacing={3} style={{ padding: 20 }}>

              {/* Header bar */}
              <Grid item xs={12}>
                <Paper variant="outlined" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <Typography variant="h5" style={{ fontWeight: 600 }}>
                      {app.first_name} {app.last_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Submitted: {app.time ? moment(app.time, 'YYYY-MM-DD HH:mm:ss').format('MM/DD/YYYY h:mm A') : 'N/A'}
                    </Typography>
                  </div>

                  <div className="credit-app-actions" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" style={{ minWidth: 150 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={currentStatus}
                        MenuProps={selectMenuProps}
                        onChange={e => handleChange('status', e.target.value)}
                        style={{ color: statusColor, fontWeight: 600 }}
                      >
                        {STATUSES.map(s => (
                          <MenuItem key={s} value={s} style={{ color: STATUS_COLORS[s], fontWeight: 600 }}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {app.customer && (
                      <Button variant="outlined" size="small" onClick={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                          window.open(`${window.location.pathname}?customer=${app.customer}`, '_blank');
                          return;
                        }
                        StateManager.openCustomerModal(app.customer);
                      }}>
                        View Customer
                      </Button>
                    )}

                    <Button variant="outlined" size="small" onClick={() => window.open(`/credit-app/${id}/print`, '_blank')}>
                      Generate PDF
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<HistoryIcon />}
                      onClick={() => history.push(`/credit-app-history?creditApp=${id}`)}
                    >
                      Send History
                    </Button>

                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<SendIcon />}
                      onClick={() => setSendOpen(true)}
                    >
                      Send to Lenders
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleSave}
                      disabled={!hasEdits || saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </Paper>
              </Grid>

              {/* Field sections — two columns on md+ */}
              {SECTIONS.map(section => (
                <Grid item xs={12} md={6} key={section.title}>
                  <Paper variant="outlined" style={{ padding: 20, height: '100%' }}>
                    <Typography
                      variant="subtitle1"
                      style={{ fontWeight: 700, borderBottom: '2px solid #1976d2', paddingBottom: 6, marginBottom: 16 }}
                    >
                      {section.title}
                    </Typography>
                    <Grid container spacing={2}>
                      {section.fields.map(field => (
                        <Grid item xs={12} sm={6} key={field.id}>
                          <TextField
                            fullWidth
                            size="small"
                            label={field.label}
                            type={field.type || 'text'}
                            value={getValue(field.id)}
                            onChange={e =>
                              handleChange(
                                field.id,
                                field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value
                              )
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              ))}

              {/* Associated Vehicle */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" style={{ padding: 20 }}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 700, borderBottom: '2px solid #1976d2', paddingBottom: 6, marginBottom: 16 }}
                  >
                    Associated Vehicle
                  </Typography>

                  {dealMatch ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {app.car_thumbnail && (
                        <img
                          src={app.car_thumbnail}
                          alt={app.car_title}
                          style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee', cursor: 'pointer' }}
                          onClick={() => StateManager.openCarModal(dealMatch.stock)}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 700, cursor: 'pointer', color: '#1976d2' }}
                          onClick={() => StateManager.openCarModal(dealMatch.stock)}
                        >
                          <DirectionsCarIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />
                          {app.car_title || `Stock #${dealMatch.stock}`}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Supplied automatically from this deal — sent from the car's Sales tab
                        </Typography>
                      </div>
                    </div>
                  ) : app.stock && app.car_title ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {app.car_thumbnail && (
                        <img
                          src={app.car_thumbnail}
                          alt={app.car_title}
                          style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee', cursor: 'pointer' }}
                          onClick={() => StateManager.openCarModal(app.stock)}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 700, cursor: 'pointer', color: '#1976d2' }}
                          onClick={() => StateManager.openCarModal(app.stock)}
                        >
                          <DirectionsCarIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />
                          {app.car_title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">Stock #{app.stock}</Typography>
                      </div>
                      <Button
                        size="small"
                        startIcon={<SwapHorizIcon />}
                        onClick={() => setCarPickerOpen(true)}
                        disabled={vehicleLinking}
                      >
                        Swap
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<LinkOffIcon />}
                        onClick={handleUnlinkVehicle}
                      >
                        Unlink
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={() => setCarPickerOpen(true)}
                      disabled={vehicleLinking}
                    >
                      {vehicleLinking ? 'Linking…' : 'Link a Car'}
                    </Button>
                  )}
                </Paper>
              </Grid>

              {/* Co-Applicant (co-buyer) */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" style={{ padding: 20 }}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 700, borderBottom: '2px solid #1976d2', paddingBottom: 6, marginBottom: 16 }}
                  >
                    Co-Applicant
                  </Typography>

                  {dealMatch && dealMatch.buyer && dealMatch.cobuyer ? (
                    coApplicant ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            style={{ fontWeight: 700, cursor: 'pointer', color: '#1976d2' }}
                            onClick={() => StateManager.openCreditAppModal(coApplicant.id)}
                          >
                            {coApplicant.first_name} {coApplicant.last_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Supplied automatically from this deal's co-buyer
                          </Typography>
                        </div>
                      </div>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        This deal's co-buyer will be linked automatically when sent
                      </Typography>
                    )
                  ) : coApplicant ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 700, cursor: 'pointer', color: '#1976d2' }}
                          onClick={() => StateManager.openCreditAppModal(coApplicant.id)}
                        >
                          {coApplicant.first_name} {coApplicant.last_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Co-buyer on this deal's bill of sale
                        </Typography>
                      </div>
                      <Button size="small" color="error" startIcon={<LinkOffIcon />} onClick={handleUnlinkCoApplicant}>
                        Unlink
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setCoApplicantPickerOpen(true)}
                    >
                      Link Co-Applicant
                    </Button>
                  )}
                </Paper>
              </Grid>

              {/* License images */}
              {(licenseUrls.front || licenseUrls.back) && (
                <Grid item xs={12}>
                  <Paper variant="outlined" style={{ padding: 20 }}>
                    <Typography
                      variant="subtitle1"
                      style={{ fontWeight: 700, borderBottom: '2px solid #1976d2', paddingBottom: 6, marginBottom: 16 }}
                    >
                      Driver's License
                    </Typography>
                    <Grid container spacing={3}>
                      {licenseUrls.front && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary" gutterBottom>Front</Typography>
                          <img
                            src={licenseUrls.front}
                            alt="License Front"
                            style={{ maxWidth: '100%', borderRadius: 4, border: '1px solid #eee' }}
                          />
                        </Grid>
                      )}
                      {licenseUrls.back && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary" gutterBottom>Back</Typography>
                          <img
                            src={licenseUrls.back}
                            alt="License Back"
                            style={{ maxWidth: '100%', borderRadius: 4, border: '1px solid #eee' }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}

            </Grid>
          )}
        </Box>
      </Dialog>

      {app && (
        <>
          <SendCreditAppDialog
            open={sendOpen}
            onClose={() => setSendOpen(false)}
            creditAppId={id}
            onSent={() => setApp(prev => ({ ...prev, status: 'sent' }))}
          />

          <CreditAppCarPickerDialog
            open={carPickerOpen}
            onClose={() => setCarPickerOpen(false)}
            onSelect={handleSelectCar}
            title={app.stock ? 'Swap Car' : 'Link a Car'}
          />

          <CreditAppSelectorDialog
            open={coApplicantPickerOpen}
            onClose={() => setCoApplicantPickerOpen(false)}
            onSelect={handleLinkCoApplicant}
            customerId={app.customer}
          />
        </>
      )}
    </ModalStackProvider>
  );
}
